// Phone Engagements list (view 'deals') — window.PhoneDeals.
//
// A card list over the SAME data the desktop grid reads: listEngagements
// with buildEngagementParams (views/deals.jsx), so a tab here selects
// exactly the rows its pill counts on desktop. What the phone drops: the
// column grid, Board, Buyers directory, bulk edit/import/export and column
// prefs — nothing here reads or writes useColumnPrefs / ui-prefs, and the
// only stored choice is the shared Mine/Team scope (window.AtlasEngScope).
// The list as it was left (for Back) is kept in memory only.
//
// Also home to PhoneNextActionSheet (window.*), the "update next action"
// sheet the phone deal page uses — a next action is a NOTE (dc_note_subtype
// 'Next Action'), never a PATCH of the two fields — and PhoneListMemo, the
// remembered-list helper the phone People list shares.

const PD_TABS = [
  ['active',   'Active',   'active'],
  ['held',     'On hold',  'on_hold'],
  ['won',      'Won',      'won'],
  ['all',      'All',      'all'],
  ['exited',   'Exited',   'exited'],
  ['archived', 'Archived', 'archived'],
];
// Sorts offered on the phone — server-sortable keys only, so the order is
// the whole tab's, not just the pages scrolled in so far.
// `dir` is the direction a sort starts in; `desc` / `asc` say what each
// direction means for that field (the Order toggle shows them).
const PD_SORTS = [
  { key: 'stage',    dir: 'desc', label: 'Pipeline stage', desc: 'Furthest along first',    asc: 'Earliest stage first' },
  { key: 'updated',  dir: 'desc', label: 'Stage change',   desc: 'Newest move first',       asc: 'Oldest move first' },
  { key: 'estClose', dir: 'asc',  label: 'Est. close',     desc: 'Latest first',            asc: 'Soonest first' },
  { key: 'fees',     dir: 'desc', label: 'Est. fees',      desc: 'Largest first',           asc: 'Smallest first' },
  { key: 'hotScore', dir: 'desc', label: 'Hot score',      desc: 'Hottest first',           asc: 'Coldest first' },
];
const PD_PAGE = 60;
const pdTap = { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' };

// ── Remembered lists (window.PhoneListMemo, shared with mobile/people.jsx) ──
// Back from a record lands on the list as it was left — same tab, search and
// sort, the pages already loaded, the same scroll position — not a fresh
// page 1 at the top (mobile/companies.jsx does the same for its list). In
// memory only, for this session.
//  · `who` (acting-as user + All teams) must match or nothing comes back;
//    `key` (who + the exact query) must match for the rows to come back.
//  · The rows show at once and are re-read quietly underneath when they may
//    be out of date: Atlas announced a change (`events`), a record was opened
//    from the list (its page can edit it without announcing it — a next
//    action is a note), or they're over two minutes old. A stale list
//    scrolled past REFRESH_MAX rows is too much to re-read; it starts over.
const PhoneListMemo = (() => {
  const FRESH_MS = 2 * 60 * 1000;
  const REFRESH_MAX = 400;
  const create = (events) => {
    // returning: a record was opened from the list and the list hasn't
    // mounted since (PhoneDeals uses it to tell Back from a tab tap).
    const m = { who: null, ui: null, key: null, rows: null, scrollTop: 0, fetchedAt: 0, changedAt: 0, returning: false };
    events.forEach((ev) => window.addEventListener(ev, () => { m.changedAt = Date.now(); }));
    return m;
  };
  const who = (activeUser) => `${(activeUser && activeUser.id) || ''}|${window.AtlasApi?.isAllTeams?.() ? 'all' : 'team'}`;
  const touch = (m) => { m.changedAt = Date.now(); };
  const stale = (m) => !m.fetchedAt || m.changedAt >= m.fetchedAt || Date.now() - m.fetchedAt > FRESH_MS;
  const rowsFor = (m, key, pageSize) => {
    if (m.key !== key || !m.rows) return null;
    return stale(m) && m.rows.page * pageSize > REFRESH_MAX ? null : m.rows;
  };
  // Called from a layout effect, so the rows are in the DOM before paint;
  // one more try next frame in case the scroller wasn't sized yet.
  const restoreScroll = (el, top) => {
    if (!el || !top) return;
    el.scrollTop = top;
    if (Math.abs(el.scrollTop - top) > 1) requestAnimationFrame(() => { el.scrollTop = top; });
  };
  // Rows carry data-row-id. anchor() notes where the first rows under the
  // top edge sit in the list's content; reanchor() moves the scroll by
  // exactly how far a quiet refresh shifted them (rows added or dropped
  // above), so the list doesn't jump. Content positions, not screen ones:
  // a fling still running while the refresh renders must not be undone.
  // The scroller sets overflow-anchor: none (SCROLL_STYLE) so the browser's
  // own anchoring can't make the same correction twice. At the very top
  // there's nothing to hold — new rows just show.
  const SCROLL_STYLE = { overflowAnchor: 'none' };
  const contentY = (el, node) => node.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
  const anchor = (el) => {
    if (!el || el.scrollTop <= 0) return null;
    const top = el.getBoundingClientRect().top;
    const out = [];
    const nodes = el.querySelectorAll('[data-row-id]');
    for (let i = 0; i < nodes.length && out.length < 5; i += 1) {
      if (nodes[i].getBoundingClientRect().bottom > top) out.push({ id: nodes[i].getAttribute('data-row-id'), y: contentY(el, nodes[i]) });
    }
    return out.length ? out : null;
  };
  const reanchor = (el, snap) => {
    if (!el || !snap) return;
    const byId = new Map();
    el.querySelectorAll('[data-row-id]').forEach((n) => byId.set(n.getAttribute('data-row-id'), n));
    const a = snap.find((s) => byId.has(s.id));
    if (!a) return;
    const dy = contentY(el, byId.get(a.id)) - a.y;
    if (Math.abs(dy) > 0.5) el.scrollTop += dy;
  };
  // Fresh rows for the stretch a refresh re-read; anything loaded past it (a
  // page that landed meanwhile) stays, minus rows now in the fresh part.
  const mergeHead = (prev, fresh, covered) => {
    if (!prev || prev.length <= covered) return fresh;
    const seen = new Set(fresh.map((r) => r.id));
    return [...fresh, ...prev.slice(covered).filter((r) => !seen.has(r.id))];
  };
  return { REFRESH_MAX, SCROLL_STYLE, create, who, touch, stale, rowsFor, restoreScroll, anchor, reanchor, mergeHead };
})();
const pdMemo = PhoneListMemo.create(['deal-updated', 'atlas-data-changed']);

const pdToday = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
// Short date for card lines ("May 3"; the year only when it isn't this one).
const pdShortDate = (iso) => {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
};

// ── Next action sheet ────────────────────────────────────────────────────────
// Body + due date; Save sits in the sheet header so the keyboard never covers
// it. `onSave({ body, date })` owns the write (POST a 'Next Action' note) and
// may throw — the sheet stays open with the error toast.
const PhoneNextActionSheet = ({ open, note, date, onSave, onClose }) => {
  const [body, setBody] = React.useState('');
  const [due, setDue] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    setBody(''); setDue(date ? String(date).slice(0, 10) : ''); setSaving(false);
  }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    if (!body.trim() || saving) return;
    setSaving(true);
    try {
      await onSave({ body: body.trim(), date: due || null });
      window.pushToast({ type: 'success', title: 'Next action updated', body: 'The previous one stays in Notes.' });
      onClose();
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Save failed', body: e.message });
      setSaving(false);
    }
  };
  const headBtn = { ...pdTap, height: 44, padding: '0 6px', border: 'none', background: 'transparent', fontSize: 16, cursor: 'pointer' };
  const control = {
    width: '100%', fontSize: 16, padding: '11px 12px', borderRadius: 10, border: '1px solid var(--border-strong)',
    background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', outline: 'none',
  };
  return (
    <window.PhoneSheet open={open} onClose={() => { if (!saving) onClose(); }} zIndex={140} maxHeight="92%">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px 6px' }}>
        <button type="button" onClick={onClose} disabled={saving} style={{ ...headBtn, color: 'var(--text-2)' }}>Cancel</button>
        <div style={{ flex: 1, textAlign: 'center', fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>Next action</div>
        <button type="button" onClick={save} disabled={saving || !body.trim()}
          style={{ ...headBtn, color: body.trim() ? 'var(--accent-text)' : 'var(--text-4)', fontWeight: 600 }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      <div style={{ padding: '4px 16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {note && (
          <div style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.45 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>Now: </span>
            {String(note).replace(/\s+/g, ' ').slice(0, 220)}
          </div>
        )}
        <textarea autoFocus value={body} onChange={(e) => setBody(e.target.value)} rows={4}
          placeholder="What happens next on this deal?"
          style={{ ...control, minHeight: 110, maxHeight: 'calc(var(--vvh, 100vh) * 0.4)', resize: 'none', lineHeight: 1.5 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 15, color: 'var(--text-2)' }}>
          <span style={{ flexShrink: 0, width: 40 }}>Due</span>
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)}
            style={{ ...control, minHeight: 46, WebkitAppearance: 'none', appearance: 'none' }} />
        </label>
        <div style={{ fontSize: 13, color: 'var(--text-4)', lineHeight: 1.45 }}>
          Replaces the deal’s current next action (DealCloud too); the old one stays in Notes.
        </div>
      </div>
    </window.PhoneSheet>
  );
};

// ── One deal card ───────────────────────────────────────────────────────────
const PhoneDealCard = React.memo(({ d, stageBySlug, onOpen }) => {
  const today = pdToday();
  const naDate = d._raw?.next_action_date ? String(d._raw.next_action_date).slice(0, 10) : null;
  const naNote = (d.nextActionNotes || '').replace(/\s+/g, ' ').trim();
  const overdue = naDate && naDate < today;
  const dueToday = naDate === today;
  const closed = window.dealIsClosed ? window.dealIsClosed(d, stageBySlug[d.stage]) : false;
  const close = d.estCloseDate && window.estCloseStatus ? window.estCloseStatus(d.estCloseDate, { closed }) : null;
  const closeColor = !close ? 'var(--text-3)' : close.status === 'overdue' ? 'var(--rose-fg)' : 'var(--amber-fg)';
  const Badge = window.EngStageBadge;
  return (
    <button type="button" onClick={() => onOpen(d.id)} data-row-id={d.id}
      style={{
        ...pdTap, display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', textAlign: 'left',
        padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--border)',
        background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', cursor: 'pointer',
      }}>
      <span style={{ paddingTop: 2, flexShrink: 0, display: 'inline-flex' }}>
        <Logo name={d.targetName || d.name} color={window.hashColor ? window.hashColor(d.targetName || d.name || d.id) : undefined} size={36} />
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 15.5, fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {d.name}
          </span>
          {d.hotScore != null && (
            <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: d.hotScore >= 8 ? 'var(--rose-fg)' : 'var(--text-3)' }}>🔥{d.hotScore}</span>
          )}
          {d.size != null && (
            <span style={{ flexShrink: 0, fontSize: 13.5, fontWeight: 500, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{window.fmtMoney(d.size)}</span>
          )}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', rowGap: 4, minWidth: 0 }}>
          {Badge
            ? <Badge slug={d.stage} name={d.stageName} onHold={d.onHold} onHoldReason={d.onHoldReason} exitName={d.exitName} />
            : <Pill sz="sm">{d.stageName || d.stage}</Pill>}
          {d.owner && d.owner !== '—' && (
            <span style={{ fontSize: 13, color: 'var(--text-3)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.owner}</span>
          )}
        </span>
        {(naDate || naNote) ? (
          <span style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {naDate && (
              <span style={{ fontWeight: 600, color: overdue ? 'var(--rose-fg)' : dueToday ? 'var(--amber-fg)' : 'var(--text-2)' }}>
                {pdShortDate(naDate)}{overdue ? ' · overdue' : dueToday ? ' · today' : ''}{naNote ? ' — ' : ''}
              </span>
            )}
            {naNote}
          </span>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--text-4)' }}>No next action</span>
        )}
        {d.estCloseDate && (
          <span style={{ fontSize: 12.5, color: closeColor, fontWeight: close ? 500 : 400 }}>
            Est. close {pdShortDate(d.estCloseDate)}
            {close ? (close.status === 'overdue' ? ' · overdue' : close.status === 'today' ? ' · today' : ` · in ${close.days}d`) : ''}
          </span>
        )}
      </span>
    </button>
  );
});

const PhoneDealSkeleton = () => (
  <div aria-hidden>
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <div key={i} style={{ display: 'flex', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
        <window.Skeleton w={36} h={36} style={{ borderRadius: 6, flexShrink: 0 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <window.Skeleton w={`${55 + ((i * 13) % 30)}%`} h={14} />
          <window.Skeleton w="40%" h={11} />
          <window.Skeleton w={`${60 + ((i * 7) % 25)}%`} h={11} />
        </div>
      </div>
    ))}
  </div>
);

const pdIsProspect = (d) => d.stage === 'prospect' || d.stage === 'ss_prospect';

// ── The page ────────────────────────────────────────────────────────────────
const PhoneDeals = ({ stages, counts, activeUser, goToDeal, dealsTabFilter, dealsStageFilter }) => {
  const mapTab = window.mapEngDeepTab || ((t) => (t === 'mine' ? 'active' : t));
  const defaultSort = window.engTabDefaultSort || ((t) => (t === 'active' ? { key: 'stage', dir: 'desc' } : { key: 'updated', dir: 'desc' }));
  const serverKeys = window.ENG_SERVER_SORT_KEYS || new Set();
  // A deep-linked sort the server can't order by would only rank the loaded
  // pages — fall back to the tab default rather than show a partial order.
  const deepSort = dealsTabFilter?.sort?.key && serverKeys.has(dealsTabFilter.sort.key)
    ? { key: dealsTabFilter.sort.key, dir: dealsTabFilter.sort.dir || 'desc' } : null;
  const deepLinked = !!(dealsTabFilter?.tab || dealsStageFilter);
  // Back from a deal, or Deals tapped again from another tab: the list as it
  // was left (PhoneListMemo above). A deep link is a new question — it
  // starts fresh. A list a deep link opened (a Today card, a pipeline stage:
  // its own scope and filters) comes back only on the way Back from a deal
  // opened from it; a later tap on the Deals tab gets the usual defaults and
  // the sticky Mine/Team choice, as before.
  const who = PhoneListMemo.who(activeUser);
  const [saved] = React.useState(() => {
    const ui = !deepLinked && activeUser?.id && pdMemo.ui && pdMemo.who === who ? pdMemo.ui : null;
    if (!ui || !ui.deep) return ui;
    // Back steps through history, so the URL already says #/deals here; a
    // tab tap still shows the page being left until App writes the hash.
    const viaBack = pdMemo.returning && window.AtlasRouter?.parseHash?.()?.view === 'deals';
    return viaBack ? ui : null;
  });
  React.useEffect(() => { pdMemo.returning = false; }, []);
  const deepOrigin = React.useRef(saved ? !!saved.deep : deepLinked);

  const [tab, setTab] = React.useState(() => (saved ? saved.tab : dealsTabFilter?.tab ? mapTab(dealsTabFilter.tab) : 'active'));
  // Mine/Team: a deep link decides its own scope (the list must match the
  // number that was tapped); otherwise the shared sticky preference.
  const [scope, setScope] = React.useState(() => {
    if (saved) return saved.scope;
    if (dealsTabFilter?.tab) return dealsTabFilter.scope || (dealsTabFilter.tab === 'mine' ? 'mine' : 'team');
    if (dealsStageFilter) return 'team';
    return window.AtlasEngScope ? window.AtlasEngScope.get(activeUser?.role) : 'mine';
  });
  const [sort, setSort] = React.useState(() => (saved ? saved.sort : deepSort || defaultSort(tab)));
  const userSorted = React.useRef(saved ? saved.userSorted : !!deepSort);
  const [stageFilter, setStageFilter] = React.useState(saved ? saved.stageFilter : (dealsStageFilter || null));
  // The desktop grid's prospect flood guard (Team scope only), on by default
  // unless a deep link landed here — its number counts every stage.
  const [hideProspect, setHideProspect] = React.useState(saved ? saved.hideProspect : !deepLinked);
  const [search, setSearch] = React.useState(saved ? saved.search : '');
  const [q, setQ] = React.useState(saved ? saved.search.trim() : '');
  React.useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const params = React.useMemo(() => (window.buildEngagementParams
    ? window.buildEngagementParams({
      tab, scope, userId: activeUser?.id, q,
      filters: { stages: stageFilter ? [stageFilter] : [] },
      sortKey: sort.key, sortDir: sort.dir,
    })
    : {}), [tab, scope, activeUser?.id, q, stageFilter, sort.key, sort.dir]);
  const paramsKey = JSON.stringify(params);
  const [resumed] = React.useState(() => (saved ? PhoneListMemo.rowsFor(pdMemo, `${who}|${paramsKey}`, PD_PAGE) : null));

  const [items, setItems] = React.useState(resumed ? resumed.items : null);   // null = first page loading
  const [total, setTotal] = React.useState(resumed ? resumed.total : 0);
  const [page, setPage] = React.useState(resumed ? resumed.page : 1);
  const [paging, setPaging] = React.useState(false);
  const [loadedAll, setLoadedAll] = React.useState(resumed ? resumed.loadedAll : false);
  const [error, setError] = React.useState(null);
  // Prospect guard: the last page fetched added no visible row (all
  // prospects). Auto-paging stops there — otherwise the sentinel stays in
  // view and every remaining page loads back to back — and a Load more
  // button takes over.
  const [stalled, setStalled] = React.useState(resumed ? resumed.stalled : false);
  const guardRef = React.useRef(false);
  const [sortOpen, setSortOpen] = React.useState(false);
  const scrollRef = React.useRef(null);
  const sentinelRef = React.useRef(null);
  const reqSeq = React.useRef(0);
  const queryGen = React.useRef(0);   // bumped by every page-1 load; a quiet refresh from before it is dropped
  const refreshSeq = React.useRef(0);
  const loadedKey = React.useRef(resumed ? paramsKey : null);   // the query `items` answers
  const fetchedAt = React.useRef(resumed ? pdMemo.fetchedAt : 0);
  const anchorSnap = React.useRef(null);
  const live = React.useRef(null);
  live.current = { items, page, error };

  // Same scope rules as the sidebar and the desktop grid: follow a change
  // made elsewhere, and let the role-derived default land once the role
  // arrives (a stored choice or a deep link always wins).
  React.useEffect(() => {
    const onScope = (e) => { if (e.detail === 'mine' || e.detail === 'team') setScope(e.detail); };
    window.addEventListener('atlas-eng-scope', onScope);
    return () => window.removeEventListener('atlas-eng-scope', onScope);
  }, []);
  React.useEffect(() => {
    if (deepLinked || saved || !activeUser?.role || !window.AtlasEngScope || window.AtlasEngScope.hasStored()) return;
    setScope(window.AtlasEngScope.get(activeUser.role));
  }, [activeUser?.role]);   // eslint-disable-line react-hooks/exhaustive-deps
  const changeScope = (s) => { setScope(s); if (window.AtlasEngScope) window.AtlasEngScope.set(s); };
  const changeTab = (t) => {
    setTab(t);
    if (!userSorted.current) setSort(defaultSort(t));
  };

  // A fresh deep link while mounted (each tap mints a new object).
  React.useEffect(() => {
    if (!dealsTabFilter?.tab) return;
    deepOrigin.current = true;
    const t = mapTab(dealsTabFilter.tab);
    setTab(t);
    setScope(dealsTabFilter.scope || (dealsTabFilter.tab === 'mine' ? 'mine' : 'team'));
    setHideProspect(false);
    const s = dealsTabFilter.sort?.key && serverKeys.has(dealsTabFilter.sort.key)
      ? { key: dealsTabFilter.sort.key, dir: dealsTabFilter.sort.dir || 'desc' } : null;
    if (s) { userSorted.current = true; setSort(s); } else if (!userSorted.current) setSort(defaultSort(t));
  }, [dealsTabFilter]);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!dealsStageFilter) return;
    deepOrigin.current = true;
    setStageFilter(dealsStageFilter); setHideProspect(false);
  }, [dealsStageFilter]);

  const fetchPage = React.useCallback(async (p, append) => {
    // "My deals" before the signed-in user is known would ask for the whole
    // team's book under the Mine label — wait for whoami instead.
    if (scope === 'mine' && !activeUser?.id) return;
    const seq = ++reqSeq.current;
    if (!append) queryGen.current += 1;
    const startedAt = Date.now();
    setPaging(true);
    if (!append) setError(null);
    try {
      const res = await window.AtlasApi.listEngagements({ ...params, page: p, limit: PD_PAGE });
      if (seq !== reqSeq.current) return;   // superseded by a newer query
      const rows = ((res && res.items) || []).map(window.AtlasData.__adaptEngagement);
      const newTotal = res?.total ?? rows.length;
      loadedKey.current = paramsKey;
      if (!append) fetchedAt.current = startedAt;
      setStalled(rows.length > 0 && guardRef.current && rows.every(pdIsProspect));
      setItems((prev) => {
        const next = append && prev ? [...prev, ...rows] : rows;
        if (rows.length === 0 || next.length >= newTotal) setLoadedAll(true);
        return next;
      });
      setTotal(newTotal);
      setPage(p);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      if (!append) { setItems([]); setTotal(0); setLoadedAll(true); setError(e.message || 'Could not load engagements'); }
      else window.pushToast({ type: 'error', title: 'Could not load more', body: e.message });
    } finally {
      if (seq === reqSeq.current) setPaging(false);
    }
  }, [params, paramsKey, scope, activeUser?.id]);

  const reload = React.useCallback(() => {
    setItems(null); setPage(1); setLoadedAll(false); setStalled(false);
    return fetchPage(1, false);
  }, [fetchPage]);

  // Re-read every page loaded so far in one request and swap the rows in
  // place: no skeleton, and the rows on screen stay put. Runs on the way
  // back to a remembered list, and on change events while the list is open.
  const refreshQuietly = React.useCallback(async () => {
    const cur = live.current;
    if (cur.items == null || cur.error || (scope === 'mine' && !activeUser?.id)) return;
    const covered = Math.min(Math.max(1, cur.page) * PD_PAGE, PhoneListMemo.REFRESH_MAX);
    const gen = queryGen.current;
    const seq = ++refreshSeq.current;
    const startedAt = Date.now();
    try {
      const res = await window.AtlasApi.listEngagements({ ...params, page: 1, limit: covered });
      if (gen !== queryGen.current || seq !== refreshSeq.current) return;
      const fresh = ((res && res.items) || []).map(window.AtlasData.__adaptEngagement);
      const newTotal = res?.total ?? fresh.length;
      anchorSnap.current = PhoneListMemo.anchor(scrollRef.current);
      fetchedAt.current = startedAt;
      setItems((prev) => PhoneListMemo.mergeHead(prev, fresh, covered));
      setTotal(newTotal);
      setLoadedAll(fresh.length < covered || fresh.length >= newTotal);
    } catch (_) { /* keep what's on screen */ }
  }, [params, scope, activeUser?.id]);
  React.useLayoutEffect(() => {
    if (!anchorSnap.current) return;
    PhoneListMemo.reanchor(scrollRef.current, anchorSnap.current);
    anchorSnap.current = null;
  }, [items]);

  // A remembered list comes back where it was scrolled to, refreshed
  // underneath only if it may be out of date. A new query starts at the top.
  React.useLayoutEffect(() => {
    if (resumed) PhoneListMemo.restoreScroll(scrollRef.current, pdMemo.scrollTop);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const firstLoad = React.useRef(true);
  React.useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      if (resumed) { if (PhoneListMemo.stale(pdMemo)) refreshQuietly(); return; }
    } else {
      try { scrollRef.current && scrollRef.current.scrollTo({ top: 0 }); } catch (_) {}
    }
    pdMemo.scrollTop = 0;   // a fresh list never inherits the last one's offset
    reload();
  }, [reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  // A list that failed or is still loading reloads instead, as it always did.
  React.useEffect(() => {
    const onChanged = () => {
      const cur = live.current;
      if (cur.items == null || cur.error) reload(); else refreshQuietly();
    };
    window.addEventListener('deal-updated', onChanged);
    window.addEventListener('atlas-data-changed', onChanged);
    return () => {
      window.removeEventListener('deal-updated', onChanged);
      window.removeEventListener('atlas-data-changed', onChanged);
    };
  }, [reload, refreshQuietly]);

  // Remember the list for the trip back from a deal.
  React.useEffect(() => {
    Object.assign(pdMemo, { who, ui: { tab, scope, sort, userSorted: userSorted.current, stageFilter, hideProspect, search, deep: deepOrigin.current } });
  }, [who, tab, scope, sort, stageFilter, hideProspect, search]);
  React.useEffect(() => {
    if (error) { pdMemo.rows = null; return; }
    if (items == null || loadedKey.current == null) return;
    Object.assign(pdMemo, { key: `${who}|${loadedKey.current}`, rows: { items, total, page, loadedAll, stalled }, fetchedAt: fetchedAt.current });
  }, [who, items, total, page, loadedAll, stalled, error]);

  const hasMore = items != null && !loadedAll && items.length < total;

  // The flood guard is for a team-wide book; Mine, or a team that is only
  // the signed-in user (team All == mine All), never hides anything.
  const soloBook = (() => {
    const t = counts?.engagement_breakdown; const m = counts?.engagement_breakdown_mine;
    return !!(t && m && t.all != null && m.all != null && t.all === m.all);
  })();
  const guardOn = hideProspect && scope === 'team' && !soloBook && !stageFilter;
  guardRef.current = guardOn;
  // Counts land after the user on a phone (the boot no longer waits for
  // them), and only they tell a solo book apart — don't stall paging on a
  // guard decision that isn't made yet.
  const autoStalled = stalled && guardOn && !!counts?.engagement_breakdown;

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || autoStalled) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !paging) fetchPage(page + 1, true);
    }, { root: scrollRef.current || null, rootMargin: '480px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, autoStalled, paging, page, fetchPage]);

  const stageBySlug = React.useMemo(() => {
    const m = {};
    (stages || []).forEach((s) => { m[s.slug] = s; });
    return m;
  }, [stages]);
  const rows = React.useMemo(() => (items || []).filter((d) => !guardOn || !pdIsProspect(d)), [items, guardOn]);

  const sortDef = PD_SORTS.find((s) => s.key === sort.key) || { label: 'Custom order' };
  const countLine = items == null ? null
    : (loadedAll && rows.length === total)
      ? `${total.toLocaleString()} deal${total === 1 ? '' : 's'}`
      : `${rows.length.toLocaleString()} shown · ${total.toLocaleString()} in this tab`;
  // Its page may change the deal without announcing it (a next action is a
  // note), so the list re-reads itself on the way back.
  const openDeal = React.useCallback((id) => {
    if (scrollRef.current) pdMemo.scrollTop = scrollRef.current.scrollTop;
    PhoneListMemo.touch(pdMemo);
    pdMemo.returning = true;
    goToDeal(id);
  }, [goToDeal]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)' }}>
      {/* Pinned controls: search, scope, status tabs. The list scrolls. */}
      <div style={{ flexShrink: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '10px 0 10px' }}>
        <div style={{ display: 'flex', gap: 8, padding: '0 16px' }}>
          <window.PhoneSearchField value={search} onChange={setSearch} placeholder="Search deals" style={{ flex: 1, minWidth: 0 }} />
          <button type="button" aria-label="Sort and filter" onClick={() => setSortOpen(true)}
            style={{ ...pdTap, flexShrink: 0, width: 44, height: 40, borderRadius: 10, border: '1px solid var(--border)',
                     background: (userSorted.current || !hideProspect) ? 'var(--accent-soft)' : 'var(--surface-2)',
                     color: (userSorted.current || !hideProspect) ? 'var(--accent-text)' : 'var(--text-2)',
                     display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Icon.Filter size={18} />
          </button>
        </div>
        <div style={{ padding: '10px 16px 0' }}>
          <window.PhoneSegmented value={scope} onChange={changeScope}
            options={[{ value: 'mine', label: 'My deals' }, { value: 'team', label: 'Team' }]} />
        </div>
        <window.PhoneChips value={tab} onChange={changeTab} style={{ marginTop: 10 }}
          // No counts on the chips: the aggregate counts every deal in the tab,
          // prospect-stage ones included, so it read as wrong next to a Team
          // list that hides them. The line under the chips counts the list.
          options={PD_TABS.map(([k, label]) => ({ value: k, label }))} />
        {(stageFilter || countLine) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px 0', fontSize: 13, color: 'var(--text-3)', minHeight: 26 }}>
            {stageFilter && (
              <button type="button" onClick={() => setStageFilter(null)}
                style={{ ...pdTap, display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 10px', borderRadius: 999,
                         border: '1px solid var(--accent)', background: 'var(--accent-soft)', color: 'var(--accent-text)', fontSize: 13, cursor: 'pointer' }}>
                {stageBySlug[stageFilter]?.name || stageFilter}<span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>×</span>
              </button>
            )}
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{countLine}</span>
            <span style={{ flexShrink: 0, color: 'var(--text-4)' }}>{sortDef.label} {sort.dir === 'asc' ? '↑' : '↓'}</span>
          </div>
        )}
      </div>

      <window.PhoneScroll ref={scrollRef} style={PhoneListMemo.SCROLL_STYLE} onScroll={(e) => { pdMemo.scrollTop = e.currentTarget.scrollTop; }}>
        {items == null && <PhoneDealSkeleton />}
        {items != null && error && (
          <window.PhoneEmpty title="Couldn’t load engagements" body={error}
            action={<Btn variant="outline" onClick={reload}>Try again</Btn>} />
        )}
        {items != null && !error && rows.length === 0 && !hasMore && (
          <window.PhoneEmpty
            title={q ? `No deals match “${q}”` : 'Nothing here'}
            body={q ? 'Search covers deal names and notes in this tab.'
              : scope === 'mine' ? 'No deals of yours in this tab. Switch to Team to see everyone’s.'
              : guardOn ? 'Prospect-stage deals are hidden — show them from the filter button.' : null} />
        )}
        {rows.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border)' }}>
            {rows.map((d) => <PhoneDealCard key={d.id} d={d} stageBySlug={stageBySlug} onOpen={openDeal} />)}
          </div>
        )}
        {hasMore && !autoStalled && (
          <div ref={sentinelRef} style={{ padding: '18px 16px', textAlign: 'center', fontSize: 13, color: 'var(--text-3)' }}>
            {paging ? 'Loading more…' : ''}
          </div>
        )}
        {/* Every page loaded but some rows were prospect-stage and hidden —
            say so (with the way back) rather than end on a short list. */}
        {!hasMore && guardOn && rows.length > 0 && items && items.length > rows.length && (
          <button type="button" onClick={() => setHideProspect(false)}
            style={{ display: 'block', width: '100%', padding: '16px', border: 'none', background: 'transparent', textAlign: 'center', fontSize: 13.5, color: 'var(--text-3)', cursor: 'pointer' }}>
            {(items.length - rows.length).toLocaleString()} prospect-stage deal{items.length - rows.length === 1 ? '' : 's'} hidden · <span style={{ color: 'var(--accent-text)', fontWeight: 600 }}>Show</span>
          </button>
        )}
        {hasMore && autoStalled && (
          <div style={{ padding: '16px 16px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 10, lineHeight: 1.4 }}>
              The next deals in this order are all prospect-stage, which are hidden — show them from the filter button.
            </div>
            <Btn variant="outline" disabled={paging} onClick={() => fetchPage(page + 1, true)}>
              {paging ? 'Loading…' : 'Load more'}
            </Btn>
          </div>
        )}
      </window.PhoneScroll>

      <window.PhoneSheet open={sortOpen} onClose={() => setSortOpen(false)} title="Sort & filter">
        {sortDef.desc && (
          <div style={{ padding: '4px 18px 10px' }}>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 6 }}>Order</div>
            <window.PhoneSegmented value={sort.dir}
              onChange={(dir) => { userSorted.current = true; setSort((s0) => ({ ...s0, dir })); setSortOpen(false); }}
              options={[{ value: 'desc', label: sortDef.desc }, { value: 'asc', label: sortDef.asc }]} />
          </div>
        )}
        <window.PhoneSectionHeader title="Sort by" style={{ padding: '6px 18px 2px' }} />
        <window.PhoneActionList items={PD_SORTS.map((s) => ({
          key: s.key, label: s.label,
          sub: s.key === sort.key ? s[sort.dir] : s[s.dir],
          trailing: s.key === sort.key ? <Icon.Check size={18} color="var(--accent-text)" /> : null,
          // Picking the current sort again flips its direction.
          onClick: () => {
            userSorted.current = true;
            setSort(s.key === sort.key ? { key: s.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key: s.key, dir: s.dir });
            setSortOpen(false);
          },
        }))} />
        {scope === 'team' && !soloBook && (
          <>
            <window.PhoneSectionHeader title="Filter" style={{ padding: '6px 18px 2px' }} />
            <window.PhoneActionList items={[{
              key: 'prospect', label: 'Show prospect-stage deals',
              sub: 'Hidden by default in Team — there are thousands',
              trailing: !hideProspect ? <Icon.Check size={18} color="var(--accent-text)" /> : null,
              onClick: () => { setHideProspect((v) => !v); setSortOpen(false); },
            }]} />
          </>
        )}
      </window.PhoneSheet>
    </div>
  );
};

Object.assign(window, { PhoneDeals, PhoneNextActionSheet, PhoneDealCard, PhoneListMemo });
